import { IS_NATIVE } from '@/lib/nativeContext';
import { isSafeRelativePath } from '@/lib/auth-redirect';
import { EVENTS, track } from '@/lib/analytics';
import { openedPage } from '@/lib/notificationTarget';

/**
 * The native shell's entry points that carry a destination:
 *
 *  1. Universal Links (iOS) / App Links (Android). The OS forwards the
 *     tapped URL to Capacitor's bridge -- iOS via SceneDelegate's
 *     `continue userActivity` (unchanged Capacitor boilerplate), Android via
 *     the AndroidManifest autoVerify intent-filter. This is the JS side
 *     turning that into an in-app navigation instead of a fresh page load.
 *  2. A tap on a native push notification. The backend puts the target in
 *     `data.url` (same field the web service worker opens); without a
 *     listener the app just came to the foreground and stayed where it was.
 */
let _attempted = false;

// Server-rendered pages live under /api/ on the same host, and the
// universal-link setup (AASA "/*"; on Android every page but the payment
// pages, which open in the browser: P0228, AndroidManifest.xml and
// public/.well-known/assetlinks.json)
// sends gr8fulprayers.com links into the app -- including the SMS
// landing pages (/api/p/<token>) and the share-card pages behind "Send via
// Messages" (/api/share/<id>). The app has no screen for those paths, so
// navigating to them showed a blank page (live report, 2026-09-21: the
// "Read the testimony" SMS link). Token pages go to /go, which asks the
// backend for the in-app destination (`inAppPath` from /api/go/resolve).
const TOKEN_PAGE = /^\/api\/(?:p|share)\/([A-Za-z0-9_-]{8,128})\/?$/;

/**
 * Decide what to do with a URL handed to the app.
 *   { navigate: '/path' }  -- a React route (or /go, which resolves one)
 *   { browser: 'https://…' } -- a server page with no in-app equivalent
 *                              (e.g. /api/contact.vcf): open it in the
 *                              in-app browser view instead of a blank screen
 */
export function inAppTargetFor(url) {
  const m = url.pathname.match(TOKEN_PAGE);
  if (m) return { navigate: `/go?t=${encodeURIComponent(m[1])}&src=applink` };
  if (url.pathname.startsWith('/api/')) return { browser: url.href };
  return { navigate: url.pathname + url.search + url.hash };
}

export async function registerNativeDeepLinks(navigate) {
  if (!IS_NATIVE || _attempted) return;
  _attempted = true;

  const openInBrowser = async (href) => {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: href });
    } catch (err) {
      console.error('In-app browser open failed:', err);
      navigate('/');
    }
  };

  const open = (rawUrl) => {
    try {
      const target = inAppTargetFor(new URL(rawUrl));
      if (target.browser) { openInBrowser(target.browser); return; }
      if (isSafeRelativePath(target.navigate)) navigate(target.navigate);
    } catch (err) {
      console.error('Deep link handling failed:', err);
    }
  };

  try {
    const { App } = await import('@capacitor/app');
    App.addListener('appUrlOpen', (data) => open(data.url));
    // A link that LAUNCHED the app (cold start) is not always replayed
    // through appUrlOpen; pick it up explicitly.
    try {
      const launch = await App.getLaunchUrl();
      if (launch?.url) open(launch.url);
    } catch (_) { /* no launch URL, or plugin too old to report one */ }
  } catch (err) {
    console.error('Native deep link init failed:', err);
  }

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const url = notification?.data?.url;
      if (typeof url !== 'string') return;
      // P0215: a push tapped on a phone counts as a notification opened, with the page it opens (never the address).
      track(EVENTS.NOTIFICATION_OPENED, { via: 'push', page: openedPage(url) });
      // Same-origin path (the usual shape) or a full URL on our own host.
      if (isSafeRelativePath(url)) { navigate(url); return; }
      try { open(url); } catch (_) { /* ignore junk */ }
    });
  } catch (err) {
    console.error('Push tap listener init failed:', err);
  }
}
